// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Centralized query key definitions for fulfillment-related queries.
 * All fulfillment query keys should be defined here to ensure consistency.
 */

/**
 * Base query key for all fulfillment queries.
 * Invalidating this key will invalidate all fulfillment-related queries.
 */
export const fulfillmentsQueryKey = ['fulfillments'] as const;

/**
 * Creates a query key for a fulfillment list with optional filters.
 *
 * @param filter - Optional filter parameters
 * @returns Query key array for the fulfillment list query
 */
export function fulfillmentListQueryKey(filter?: unknown) {
  return filter ? [...fulfillmentsQueryKey, filter] : fulfillmentsQueryKey;
}

export function fulfillmentCycleTimeReportQueryKey(filterString: string) {
  return [...fulfillmentsQueryKey, 'cycle-time-report', filterString] as const;
}

/**
 * Creates a query key for a single fulfillment detail by key.
 *
 * @param fulfillmentKey - The unique key of the fulfillment order
 * @returns Query key array for the fulfillment detail query
 */
export function fulfillmentDetailQueryKey(fulfillmentKey: string) {
  return [...fulfillmentsQueryKey, fulfillmentKey] as const;
}

/**
 * Creates a query key for files associated with a fulfillment order.
 *
 * @param fulfillmentKey - The unique key of the fulfillment order
 * @returns Query key array for the fulfillment files query
 */
export function fulfillmentFilesQueryKey(fulfillmentKey: string) {
  return [...fulfillmentsQueryKey, fulfillmentKey, 'files'] as const;
}

/**
 * Creates a query key for URL links associated with a fulfillment order.
 *
 * @param fulfillmentKey - The unique key of the fulfillment order
 * @returns Query key array for the fulfillment URL links query
 */
export function fulfillmentURLLinksQueryKey(fulfillmentKey: string) {
  return [...fulfillmentsQueryKey, fulfillmentKey, 'url-link'] as const;
}
